import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { AppButton, AppInputTime, AppMultiSelect, AppSelect, SearchTextField } from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, getServiceTypeLabel } from '@/constants/serviceType';
import {
  EMPTY_FILTERS,
  INBOUND_KIND,
  INBOUND_KIND_LABEL,
  LEVEL_COLUMN_TITLE,
  LEVEL_LABEL,
  NOTIFICATION_TAB,
  PATIENT_COLUMN,
  hasActiveFilters,
  type InboundKind,
  type NotificationFilters,
  type NotificationLevel,
  type NotificationTab,
} from '@/types/admin-notification-center';

/**
 * 処理時間 bounds travel as 'YYYY-MM-DDTHH:mm' and are edited as two controls: a 和暦 date and a native
 * clock. Splitting them here rather than reaching for a datetime picker keeps the date field identical to
 * every other date on the product — the pickers are 和暦 everywhere and a Western datetime widget in one
 * filter would be the odd one out.
 */
const splitDate = (value: string): string => value.slice(0, 10);
const splitTime = (value: string): string => (value.length >= 16 ? value.slice(11, 16) : '');

/**
 * A DATE WITH NO TIME MEANS THE WHOLE DAY — 00:00 on the lower bound, 23:59 on the upper.
 *
 * Without this, typing only 「8月1日」 in both boxes would ask for the instant 08-01T00:00 twice and return
 * nothing. Anyone typing a date and no clock means the day, and the day is what they get until they say
 * otherwise.
 */
const join = (date: string, time: string, edge: 'from' | 'to'): string => {
  if (!date) return '';
  return `${date}T${time || (edge === 'from' ? '00:00' : '23:59')}`;
};

const READ_OPTIONS = [
  { label: 'すべて', value: 'all' },
  { label: '未読のみ', value: 'unread' },
  { label: '既読のみ', value: 'read' },
];

/**
 * 種別 — MULTI-SELECT. 👤 Owner's manager, 2026-08-27.
 *
 * No 「すべて」 option, on purpose: an empty box already means all, and offering a value that means the same
 * as no value gives two ways to say one thing. Order puts エラー first — that is the reason the filter is
 * opened.
 */
const LEVEL_OPTIONS: { label: string; value: NotificationLevel }[] = [
  { label: LEVEL_LABEL.error, value: 'error' },
  { label: LEVEL_LABEL.info, value: 'info' },
  { label: LEVEL_LABEL.success, value: 'success' },
];

/**
 * 種別 on 審査システムからの通知 — the FOUR kinds 審査 actually sends.
 *
 * NOT SIX. The BD sheet also lists 取込エラー and 状態不一致, and RAH does record both — as sync_unresolved
 * and reconcile_drift, shown on the older 同期通知 screen. Neither is written under this tab's category, so
 * offering them here would put two values in the dropdown that can only ever return an empty list, which
 * reads as a broken filter rather than as an empty inbox. Add them the day the rows can reach this tab.
 *
 * 返戻 first: it is the only one that asks the clinic to do something, so it is the reason the filter is
 * opened — the same ordering rule as エラー on the sending tabs.
 */
const KIND_OPTIONS: { label: string; value: InboundKind }[] = [
  { label: INBOUND_KIND_LABEL[INBOUND_KIND.RETURNED], value: INBOUND_KIND.RETURNED },
  { label: INBOUND_KIND_LABEL[INBOUND_KIND.RETURN_CANCELLED], value: INBOUND_KIND.RETURN_CANCELLED },
  { label: INBOUND_KIND_LABEL[INBOUND_KIND.PARTIAL_UNPAID], value: INBOUND_KIND.PARTIAL_UNPAID },
  { label: INBOUND_KIND_LABEL[INBOUND_KIND.PAYMENT_CANCELLED], value: INBOUND_KIND.PAYMENT_CANCELLED },
];

/**
 * 施術区分 — SINGLE-SELECT, one of すべて / はり・きゅう / マッサージ.
 *
 * It was a multi-select. A row is one 確定 press for ONE service, so ticking both asked for exactly what
 * ticking neither already asked for — a second way to spell 「all」 that also made the header chip lie
 * (summarise only printed it at length === 1, so the two-ticked state narrowed nothing and said nothing).
 *
 * 「すべて」 IS AN OPTION HERE, unlike 種別 above, and the difference is not an inconsistency: a multi-select
 * has an empty state the user can reach by un-ticking, a dropdown does not. Without a value meaning 「all」
 * this filter would be one-way — choosable but undoable only through 条件をクリア, which drops every other
 * condition with it.
 */
const SERVICE_OPTIONS = [
  { label: 'すべて', value: '' },
  { label: `${SERVICE_TYPE_A2}：はり・きゅう`, value: SERVICE_TYPE_A2 },
  { label: `${SERVICE_TYPE_R2}：マッサージ`, value: SERVICE_TYPE_R2 },
];

const READ_LABEL: Record<string, string> = { unread: '未読のみ', read: '既読のみ' };

/** 和暦 + clock, and the clock only when it was actually typed — 「〜08月27日 00:00」 says nothing extra. */
const jp = (value: string): string => {
  if (!value) return '';
  const d = dayjs(value.slice(0, 10));
  const t = splitTime(value);
  return t && t !== '00:00' && t !== '23:59' ? `${convGtJDate(d)} ${t}` : convGtJDate(d);
};

/**
 * What is narrowing the list right now, as short phrases.
 *
 * Shown NEXT TO the collapsed toggle, and that is the whole reason the panel is allowed to hide: a closed
 * box plus a short list is 「why am I only seeing 6 rows?」. With the conditions spelled out on the same
 * line, hiding the controls costs the reader nothing.
 */
const summarise = (f: NotificationFilters, tab: NotificationTab): string[] => {
  const chips: string[] = [];

  if (f.from && f.to) chips.push(`処理時間: ${jp(f.from)}〜${jp(f.to)}`);
  else if (f.from) chips.push(`処理時間: ${jp(f.from)}〜`);
  else if (f.to) chips.push(`処理時間: 〜${jp(f.to)}`);

  if (f.levels.length > 0 && tab !== NOTIFICATION_TAB.INBOUND)
    chips.push(`${LEVEL_COLUMN_TITLE}: ${f.levels.map(l => LEVEL_LABEL[l]).join('・')}`);
  // Same label, the other meaning — the inbound tab's 種別 is the kind, not the level, so it gets its own
  // chip guarded on the same tab test. Without this the chip strip would say nothing while the list is
  // narrowed, which is the one thing the strip exists to prevent.
  if (f.kinds.length > 0 && tab === NOTIFICATION_TAB.INBOUND)
    chips.push(`${LEVEL_COLUMN_TITLE}: ${f.kinds.map(k => INBOUND_KIND_LABEL[k]).join('・')}`);
  if (f.read !== 'all') chips.push(`既読状態: ${READ_LABEL[f.read]}`);
  const isMaster = tab === NOTIFICATION_TAB.MASTER;
  if (f.clinicNumber.trim())
    chips.push(`${isMaster ? 'コード' : '治療院ID'}: ${f.clinicNumber.trim()}`);
  if (f.clinicName.trim()) chips.push(`${isMaster ? '名称' : '治療院名'}: ${f.clinicName.trim()}`);
  if (f.serviceType) chips.push(`施術区分: ${getServiceTypeLabel(f.serviceType)}`);
  if (f.patient.trim()) chips.push(`受診者: ${f.patient.trim()}`);

  return chips;
};

/**
 * One bound = one 和暦 date + one clock, written back as a single ISO-ish string.
 *
 * DECLARED AT MODULE SCOPE, and that is load-bearing rather than tidiness. It used to sit inside the panel's
 * own body, which makes it a NEW component TYPE on every render — React compares element types, sees a
 * different one, and DISCARDS the subtree to mount a fresh copy instead of updating what is there. The panel
 * re-renders on every setDraft, i.e. on every date click and every keystroke in the clock, so:
 *
 *   the 和暦 popup closed the instant a day was clicked (the picker it belonged to no longer existed), and
 *   the native <input type="time"> lost focus after its FIRST digit — the minutes could never be typed, so
 *   a time could not be entered at all.
 *
 * Everything it needs now arrives as a prop. `onChange` is the panel's own `set`, narrowed to the two string
 * bounds; passing it keeps the draft in one place rather than giving this component state of its own.
 */
const Bound = ({
  edge,
  draft,
  onChange,
  onEnter,
}: {
  edge: 'from' | 'to';
  draft: NotificationFilters;
  onChange: (edge: 'from' | 'to', next: string) => void;
  onEnter: () => void;
}) => (
  <div className="flex items-center gap-1.5">
    <RA2NDatePicker
      value={splitDate(draft[edge]) ? convGtJDate(dayjs(splitDate(draft[edge]))) : ''}
      onChange={v => onChange(edge, join(dateValueToIso(v) ?? '', splitTime(draft[edge]), edge))}
      endDate={edge === 'from' ? splitDate(draft.to) || undefined : undefined}
      startDate={edge === 'to' ? splitDate(draft.from) || undefined : undefined}
      placeholder="日付を入力"
      // A REAL WIDTH, not `flex-1`. This row is a flex item whose own width comes from its content, so
      // `flex-1` (basis 0) gave the date box nothing to grow from and it collapsed to its calendar icon --
      // the date could not be read or typed, only the clock beside it showed. 190px is the width every other
      // 和暦 filter on the product uses, and 令和00年00月00日 fits it.
      className="w-[190px] shrink-0"
      onEnter={onEnter}
    />
    {/* THE WIDTH LIVES ON THIS WRAPPER, not on the input. AppInputTime hard-codes `w-full` ahead of the
        caller's class, and two width utilities on one element are resolved by stylesheet order rather than
        by attribute order — `w-full` won, so a width passed straight in did nothing and the clock stretched
        across the row. Sizing the parent cannot be overruled that way.

        160px, SIZED FOR THE 12-HOUR RENDERING. Chrome prints <input type="time"> in the format of the
        OPERATING SYSTEM's region, not the page's — so the same build shows `23:59` on one machine and
        `12:00 AM` on another, and the second is roughly 26px wider. 140px fit the first and clipped the
        second: the value lost its leading digit and read 「)4:00 AM」 — reported on both the admin and the
        clinic portal, since this panel serves both.

        index.scss keeps the picker indicator at `opacity: 0` (KEiLOG's 施術記録 (Visit record) clock needs
        it), so it still takes about 29px. Chrome (2026-10-22) asks for about 123px at `23:59`. The 12-hour
        figures are that width plus the measured text gap of 「12:00 AM」 (+22px) or 「12:00 午前」 (+27px),
        not a 12-hour render: about 146px and 151px. 160px still clears both. */}
    <div className="w-[160px] shrink-0">
      <AppInputTime
        value={splitTime(draft[edge])}
        onChange={t => onChange(edge, join(splitDate(draft[edge]), t, edge))}
        disabled={!splitDate(draft[edge])}
      />
    </div>
  </div>
);

interface NotificationCenterFilterPanelProps {
  tab: NotificationTab;
  /**
   * Offer 治療院ID / 治療院名. TRUE for the admin portal, FALSE for the 治療院 one, where both would be a
   * search box with exactly one legal answer. 👤 owner, 2026-09-02.
   */
  showClinicFilters?: boolean;
  /** The filters currently APPLIED (the ones the list was fetched with). */
  value: NotificationFilters;
  onSearch: (next: NotificationFilters) => void;
  disabled?: boolean;
}

/**
 * 検索条件, behind a disclosure.
 *
 * CLOSED BY DEFAULT. Most visits to this screen are 「what came in?」, not 「let me search」, and a permanent
 * filter block pushed the first row of the list below the fold. It opens on its own when the URL already
 * carries filters — a shared link lands with its conditions shown, not merely applied — and it closes itself
 * after 検索, because the chips on the header then say what is in force.
 *
 * Typing edits a LOCAL draft and nothing happens until 検索 (or Enter). Filters that fire on every keystroke
 * make the list flicker under the cursor and, on a screen whose rows carry buttons, move the button you were
 * aiming at.
 *
 * THE CONTROLS ARE THE SAME FIVE ON EVERY TAB — 👤 owner's manager, 2026-08-27: 種別 · 処理時間 · 治療院ID ·
 * 治療院名 · 既読状態. Two extras appear where they can answer something: 施術区分 on レセプト同期, 受診者 on
 * 審査システムからの通知. A panel that rearranges itself per tab costs the reader a re-read every time they
 * switch, so the shared five keep their positions.
 */
const NotificationCenterFilterPanel = ({
  tab,
  showClinicFilters = true,
  value,
  onSearch,
  disabled,
}: NotificationCenterFilterPanelProps) => {
  const [draft, setDraft] = useState<NotificationFilters>(value);
  const [open, setOpen] = useState<boolean>(() => hasActiveFilters(value));

  useEffect(() => {
    setDraft(value);
  }, [value, tab]);

  const chips = useMemo(() => summarise(value, tab), [value, tab]);

  const set = <K extends keyof NotificationFilters>(key: K, next: NotificationFilters[K]) =>
    setDraft(prev => ({ ...prev, [key]: next }));

  const submit = () => {
    onSearch(draft);
    setOpen(false);
  };

  const clear = () => {
    setDraft(EMPTY_FILTERS);
    onSearch(EMPTY_FILTERS);
  };

  /**
   * Drop 処理時間 — BOTH ends — and nothing else.
   *
   * Neither control carries a clear of its own: RA2NDatePicker has no allowClear, and the `appearance: none`
   * every time input needs (index.scss) strips the browser's. So the only way out of a period was
   * 条件をクリア, which also drops 種別 / 治療院ID / 名称 / 既読状態 — the wrong size of undo for one
   * condition.
   *
   * ONE BUTTON FOR THE PAIR, not one per end: a period is a single condition and is read as one (the chip
   * says 「処理時間: A〜B」). Clearing half of it leaves an open-ended range, which is a different search
   * rather than a cleared one. Both ends go in one setDraft so the list never renders a half-cleared period.
   */
  const clearPeriod = () => setDraft(prev => ({ ...prev, from: '', to: '' }));

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      submit();
    }
  };

  const isMaster = tab === NOTIFICATION_TAB.MASTER;
  const isRezept = tab === NOTIFICATION_TAB.REZEPT;

  // The two free-text boxes search the same PAIR the table shows, so they take the same words: マスタ同期
  // carries whichever master the run is about (治療院 or 保険者), the other tabs always a clinic. A box
  // labelled 治療院ID that also matches 保険者番号 would be wrong twice over — wrong word, and it hides
  // that searching a 保険者 number is even possible.
  const codeLabel = isMaster ? 'コード' : '治療院ID';
  const nameLabel = isMaster ? '名称' : '治療院名';
  const isInbound = tab === NOTIFICATION_TAB.INBOUND;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/70">
      {/* Header — always visible. The whole strip is the hit area, not just the chevron. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 transition hover:text-blue-600"
        >
          <i className="fa fa-search text-xs" aria-hidden="true" />
          検索条件
          <i
            className={`fa fa-angle-down text-base transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {/* Chips ride on the header so a closed panel still says what it is doing. Hidden while it is open,
            where the controls themselves already show it. */}
        {!open && chips.length > 0 && (
          <>
            {chips.map(chip => (
              <span
                key={chip}
                className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs text-blue-700"
              >
                {chip}
              </span>
            ))}
            <button
              type="button"
              className="text-xs text-gray-500 underline hover:text-gray-700"
              onClick={clear}
            >
              条件をクリア
            </button>
          </>
        )}

        {!open && chips.length === 0 && (
          <span className="text-xs text-gray-400">すべての通知を表示中</span>
        )}
      </div>

      {/* Collapse via grid-template-rows 1fr↔0fr — the same idiom the admin sidebar uses, and the only one
          that animates to CONTENT height without hard-coding a px max-height that clips the taller tabs.
          overflow-hidden is safe here: the date picker and both selects render their popups through a
          portal on document.body, so nothing that opens downward is clipped by this box. */}
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className={`min-h-0 overflow-hidden ${open ? '' : 'pointer-events-none'}`}>
          <div className="grid grid-cols-1 items-end gap-x-4 gap-y-3 border-t border-gray-200 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
            {/* 処理時間 — DATE AND TIME on both ends. 👤 Owner's manager, 2026-08-27.
                A sync run is minutes long, so a whole-day bound cannot separate the 07:00 batch from the
                19:00 one — which is the usual reason someone opens this filter at all. The clock is
                disabled until a date is picked: a time with no day is not a bound. */}
            <div className="md:col-span-2 xl:col-span-4">
              <label className="mb-1 block text-sm text-gray-600">処理時間</label>
              <div className="flex flex-wrap items-center gap-2">
                <Bound edge="from" draft={draft} onChange={set} onEnter={submit} />
                <span className="shrink-0 text-sm text-gray-400">〜</span>
                <Bound edge="to" draft={draft} onChange={set} onEnter={submit} />
                {/* Hidden until the period holds something, for the same reason 条件をクリア is: a button
                    that would do nothing reads as a broken button. `||` not `&&` — one end on its own is
                    still a period worth being able to drop. */}
                {(draft.from || draft.to) && (
                  <button
                    type="button"
                    aria-label="処理時間をクリア"
                    title="処理時間をクリア"
                    onClick={clearPeriod}
                    className="ml-1 shrink-0 rounded p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                  >
                    <i className="fa fa-times text-sm" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            {/* 種別 — ON EVERY TAB, but not the same filter.
                On the two sending tabs it is the LEVEL (情報 / 成功 / エラー); on 審査システムからの通知
                nothing can fail, so a level filter there would offer three values where every row is the
                same one. What a reader wants on that tab is WHICH of the four things 審査 sent, which is
                what the second control below filters. Same label, same shape, one meaning per tab. */}
            <div>
              <label className="mb-1 block text-sm text-gray-600">{LEVEL_COLUMN_TITLE}</label>
              {isInbound ? (
                <AppMultiSelect
                  className="w-full"
                  value={draft.kinds}
                  options={KIND_OPTIONS}
                  placeholder="すべて"
                  allowClear
                  onChange={v => set('kinds', ((v as InboundKind[]) ?? []))}
                />
              ) : (
                <AppMultiSelect
                  className="w-full"
                  value={draft.levels}
                  options={LEVEL_OPTIONS}
                  placeholder="すべて"
                  allowClear
                  onChange={v => set('levels', ((v as NotificationLevel[]) ?? []))}
                />
              )}
            </div>

            {/* コード / 名称 — FREE TEXT, both. 👤 Owner's manager, 2026-08-27.
                The 治療院ID control used to be a dropdown built from the clinics present in the loaded page,
                so typing a code with no row today found nothing and read as broken. Free text asks the
                server, which knows about every clinic.

                ABSENT on the 治療院 portal: inside one clinic both boxes have one legal answer. */}
            {showClinicFilters && (
              <>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">{codeLabel}</label>
                  <SearchTextField
                    value={draft.clinicNumber}
                    placeholder="部分一致"
                    onChange={e => set('clinicNumber', e.target.value)}
                    onKeyDown={onEnter}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">{nameLabel}</label>
                  <SearchTextField
                    value={draft.clinicName}
                    placeholder="部分一致"
                    onChange={e => set('clinicName', e.target.value)}
                    onKeyDown={onEnter}
                  />
                </div>
              </>
            )}

            <div>
              <label className="mb-1 block text-sm text-gray-600">既読状態</label>
              <AppSelect
                className="w-full"
                value={draft.read}
                options={READ_OPTIONS}
                onChange={v => set('read', v as NotificationFilters['read'])}
              />
            </div>

            {/* 施術区分 — レセプト同期 only. A run is one 確定 press for one service. */}
            {isRezept && (
              <div>
                <label className="mb-1 block text-sm text-gray-600">施術区分</label>
                <AppSelect
                  className="w-full"
                  value={draft.serviceType}
                  options={SERVICE_OPTIONS}
                  onChange={v => set('serviceType', (v as string) ?? '')}
                />
              </div>
            )}

            {/* ONE box for BOTH 受診者 columns — and for the カナ, which is not a column at all.
                Whoever is looking for a person is holding either the name off a 帳票 or the number off a
                レセプト, and making them work out which box it belongs in is friction for nothing. It is
                also what 返戻処理 already does with the same three values.
                Not offered on レセプト同期: a row there is ONE RUN covering every patient in a 確定 press,
                so 「find this person's rezept」 has nothing to land on. */}
            {isInbound && (
              <div>
                <label className="mb-1 block text-sm text-gray-600">
                  {PATIENT_COLUMN.NAME}・{PATIENT_COLUMN.SERIAL}
                </label>
                <SearchTextField
                  value={draft.patient}
                  placeholder="氏名・カナ・番号（部分一致）"
                  onChange={e => set('patient', e.target.value)}
                  onKeyDown={onEnter}
                />
              </div>
            )}

            <div className="flex items-center gap-2 md:col-span-2 xl:col-span-4 xl:justify-end">
              <AppButton type="primary" icon={<Search />} onClick={submit} disabled={disabled}>
                検索
              </AppButton>
              {/* Hidden until something IS filtered: an always-visible クリア on a clean form invites a press
                  that does nothing, and users read that as the screen being broken. */}
              {hasActiveFilters(draft) && (
                <AppButton onClick={clear} disabled={disabled}>
                  条件をクリア
                </AppButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenterFilterPanel;
